import ReactMarkdown, { type Components, type Options } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkCjkFriendly from 'remark-cjk-friendly';
import remarkCjkFriendlyGfmStrikethrough from 'remark-cjk-friendly-gfm-strikethrough';
import remend from 'remend';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';

/**
 * The render-time link policy for model output.
 *
 * Issue descriptions, comments and task names are user-authored and reach the
 * model as data, so anything the model reproduces in an answer arrives here
 * wearing the product's own chrome. `react-markdown`'s default `urlTransform`
 * keeps `http(s)` hrefs, which is enough for an issue body containing
 * `[こちら](https://evil.example/login)` to be summarised into a clickable,
 * same-tab anchor to an attacker's origin inside the assistant's bubble.
 *
 * External links are rejected by design — `resolveAiLink` in `@utils/aiChat`
 * is the same rule at the navigation boundary — and no answer has ever needed
 * one: the tools return data, not URLs. So an anchor keeps its visible label
 * and loses the href entirely. That also covers GFM's autolink literals, where
 * a bare `https://…` sitting in the answer text becomes an anchor with no
 * markdown syntax involved at all.
 *
 * Images go the same way, and more urgently: an `<img>` needs no click to
 * reach an attacker's origin — it fires on render, which makes it a silent
 * exfiltration channel rather than a phishing one. Only the alt text survives.
 *
 * Raw HTML in the markdown needs no handling: `rehype-raw` is deliberately not
 * installed, so react-markdown escapes it instead of parsing it.
 */
const TEXT_ONLY_LINKS: Components = {
  a: ({ children }) => <>{children}</>,
  img: ({ alt }) => <>{alt}</>,
  // A wide code block scrolls sideways on its own, not the whole answer.
  pre: ({ children }) => (
    <ScrollArea orientation="horizontal">
      <pre>{children}</pre>
    </ScrollArea>
  ),
};

/**
 * The parse, tuned for Japanese answers (answer-surface spec, §3).
 *
 * `singleTilde: false` because the model writes `~` for RANGES — "3~5日、7~9日"
 * — and GFM's default reads two of them in one sentence as a strikethrough of
 * everything between. `~~double~~` still strikes, which is what a GFM author
 * means by it.
 *
 * `remark-cjk-friendly` because CommonMark's flanking rules do not see a `**`
 * that closes after `）`, `。` or `」` and runs straight into the next character —
 * the shape of nearly every bolded clause in a Japanese answer, which rendered
 * as literal asterisks.
 *
 * THE STRIKETHROUGH PLUGIN TAKES `singleTilde: false` TOO. It replaces GFM's
 * strikethrough tokenizer with its own, whose default is single-tilde ON — so
 * without the option here the ranges strike again, with remark-gfm's own
 * setting silently overridden (verified against 2.3.1).
 */
const REMARK_PLUGINS: Options['remarkPlugins'] = [
  [remarkGfm, { singleTilde: false }],
  remarkCjkFriendly,
  [remarkCjkFriendlyGfmStrikethrough, { singleTilde: false }],
];

export interface IAiMarkdownProps {
  /** One assistant answer, as the markdown the model emitted. */
  content: string;
  /** Wrapper class — each surface owns its own markdown typography. */
  className?: string;
  /**
   * This text is still being written: the tail of the answer while its turn
   * streams. Its unclosed `**`, `` ` `` or `[` are closed for the render, so a
   * half-written bold does not flash as raw asterisks until its pair arrives.
   * Only ever the LAST prose part of the answer still arriving — a complete
   * answer renders exactly as written — and only after `answerParts` has held
   * back a half-written drawing marker, so the repair never sees `{{draw`.
   */
  streaming?: boolean;
}

/**
 * Renders one assistant answer as markdown (GFM: tables, task lists,
 * strikethrough, autolinks).
 *
 * The single place both chat surfaces — the AI Agent page transcript and the
 * dashboard card — turn model output into DOM, so the link policy above holds
 * for both and cannot drift between them.
 */
export function AiMarkdown(props: IAiMarkdownProps) {
  const { content, className, streaming = false } = props;
  // No maths in this product, and a lone `$` is a price, not an equation.
  const text = streaming ? remend(content, { katex: false, inlineKatex: false }) : content;
  return (
    <div className={className}>
      <ReactMarkdown remarkPlugins={REMARK_PLUGINS} components={TEXT_ONLY_LINKS}>
        {text}
      </ReactMarkdown>
    </div>
  );
}
